import ActionButton from 'components/ActionButton';
import { arrayOf, func, object, string } from 'prop-types';
import React from 'react';
import { path } from 'ramda';

import StatusForm from 'routes/Documents/routes/Status/components/StatusForm';
import EQ5DForm from 'routes/Documents/routes/QualityOfLife/components/EQ5DForm';
import MSNQForm from 'routes/Documents/routes/Scores/components/MSNQForm';
import FSMCForm from 'routes/Documents/routes/Scores/components/FSMCForm';
import MSNQInfo from 'routes/Documents/routes/Scores/components/MSNQInfo';
import calculateMSNQTotals from 'routes/Documents/routes/Scores/calculateMSNQ';
import calculateFSMCTotals from 'routes/Documents/routes/Scores/calculateFSMC';
import FSMCInfo from 'routes/Documents/routes/Scores/components/FSMCInfo';

import FormDialog from '../../FormDialog';
import RightSide from './RightSide';
import ContentColumns from '../components/ContentColumns';
import { getAllCommitsData } from 'lib/documentHandling';
import normalize from 'lib/normalize';
import View2 from './View2';
import View3 from './View3';
import View4 from './View4';

const LeftSideTexts = ({ type, fm }, { formValues }) => {
  return (
    <div>
      {type === 'status' ? (
        <div>
          <h1>{fm('onlineRehabilitation.rehab8.1.hello')}</h1>
          <br />
          <h2>{fm('onlineRehabilitation.rehab8.1.h2')}</h2>
          <br />
          <div>{fm('onlineRehabilitation.rehab8.1.text')}</div>
          <br />
        </div>
      ) : type === 'eq5d' ? (
        <div>
          <h2>{fm('onlineRehabilitation.rehab8.2.h2')}</h2>
          <br />
          <div>{fm('onlineRehabilitation.rehab8.2.text')}</div>
          <br />
        </div>
      ) : type === 'msnq' ? (
        <div>
          <h2>{fm('onlineRehabilitation.rehab8.3.h2')}</h2>
          <br />
          <div>{fm('onlineRehabilitation.rehab8.3.text')}</div>
          <br />
        </div>
      ) : type === 'fsmc' ? (
        <div>
          <h2>{fm('onlineRehabilitation.rehab8.4.h2')}</h2>
          <br />
          <div>{fm('onlineRehabilitation.rehab8.4.text')}</div>
          <br />
        </div>
      ) : (
        <div>
          <h2>{fm('onlineRehabilitation.rehab8.5.h2')}</h2>
          <br />
          <div>{fm('onlineRehabilitation.rehab8.5.text')}</div>
        </div>
      )}
    </div>
  );
};
LeftSideTexts.contextTypes = {
  formValues: object,
};
LeftSideTexts.propTypes = {
  type: string,
  fm: func,
};

class Contents extends React.Component {
  state = { dialogOpen: false, type: null, infoComponent: null, editingId: null, view: 1 };

  openSubForm = type => {
    const { create, editingDocumentId } = this.context;
    create(type, { parentDocumentId: editingDocumentId });
    this.setState({ dialogOpen: true, type, editingId: 'newDocument' });
  };

  openSubFormEdit = id => {
    const { edit } = this.context;
    const { documents } = this.props;
    const editingDocument = documents.find(d => d.documentId === id);
    if (editingDocument) {
      const type = editingDocument.documentType;
      edit(type, id, getAllCommitsData(editingDocument));
      this.setState({ dialogOpen: true, type, editingId: id });
    }
  };

  onCancel = (editingDocument, commitId) => {
    if (editingDocument) {
      const { cancel } = this.context;
      cancel(this.state.type, editingDocument.documentId, commitId);
    }
    this.setState({ dialogOpen: false, type: null, editingId: null });
  };
  onConfirm = (editingDocument, commitId, currentData) => {
    const { type, viewingId } = this.state;
    const { done, onChange, formValues } = this.context;

    if (editingDocument) {
      // Finish subdocument
      done(type, editingDocument.documentId, commitId, currentData);

      const idAlreadySaved = path(['rehab8', type], formValues);

      // Update ID in main document.
      !idAlreadySaved && onChange({ [type]: editingDocument.documentId });
    }

    // Get info components for msnq and fsmc
    if (type === 'msnq') {
      const { documents } = this.props;
      const msnqDocument = documents.find(
        d => d.documentId === path(['documentId'], editingDocument) || d.documentId === viewingId,
      );
      const msnqTotalsDoc = calculateMSNQTotals([msnqDocument]);
      const handleMSNQDoc = normalize(msnqTotalsDoc)[0];
      this.setState({ infoComponent: <MSNQInfo document={handleMSNQDoc} /> });
    } else if (type === 'fsmc') {
      const { documents } = this.props;
      const fsmcDocument = documents.find(
        d => d.documentId === path(['documentId'], editingDocument) || d.documentId === viewingId,
      );
      const fsmcTotalsDoc = calculateFSMCTotals([fsmcDocument]);
      this.setState({ infoComponent: <FSMCInfo document={fsmcTotalsDoc[0]} /> });
    } else {
      this.setState({ dialogOpen: false, type: null, editingId: null, viewingId: null });
    }
  };

  onCloseInfo = () => {
    this.setState({ dialogOpen: false, type: null, infoComponent: null, editingId: null, viewingId: null });
  };

  onView = (id, type) => {
    this.setState({ dialogOpen: true, type, viewingId: id });
  };

  advanceView = () => {
    const { setFinished } = this.context;
    if (this.state.view === 3) {
      setFinished();
    }
    this.setState({ view: this.state.view + 1 });
  };

  backView = () => {
    this.setState({ view: this.state.view - 1 });
  };

  componentDidMount() {
    const finished = path(['formValues', 'rehab8', 'finished'], this.context);
    // If this session is finished, navigate to last view
    if (finished) this.setState({ view: 4 });
  }

  render() {
    const { type, infoComponent, editingId, viewingId, view } = this.state;
    const { documents } = this.props;
    const { formValues, fm } = this.context;

    const statusId = path(['rehab8', 'status'], formValues);
    const eq5dId = path(['rehab8', 'eq5d'], formValues);
    const msnqId = path(['rehab8', 'msnq'], formValues);
    const fsmcId = path(['rehab8', 'fsmc'], formValues);

    const filledNames = [statusId, eq5dId, msnqId, fsmcId].filter(f => f);
    const currentType =
      filledNames.length === 0
        ? 'status'
        : filledNames.length === 1
          ? 'eq5d'
          : filledNames.length === 2
            ? 'msnq'
            : filledNames.length === 3
              ? 'fsmc'
              : 'done';

    return (
      <div>
        {view === 1 && (
          <ContentColumns
            firstColumn={
              <div>
                <LeftSideTexts type={currentType} fm={fm} />
                {currentType !== 'done' && (
                  <ActionButton
                    localeId={'onlineRehabilitation.rehab8.fillButton.' + currentType}
                    onClick={() => this.openSubForm(currentType)}
                    filledSecondary
                  />
                )}
              </div>
            }
            secondColumn={
              <RightSide
                ids={{ status: statusId, eq5d: eq5dId, msnq: msnqId, fsmc: fsmcId }}
                startEdit={this.openSubFormEdit}
                startView={this.onView}
              />
            }
          />
        )}

        {view === 2 && <View2 />}
        {view === 3 && <View3 />}
        {view === 4 && <View4 />}

        <FormDialog
          dialogOpen={this.state.dialogOpen}
          type={type}
          form={
            type === 'status'
              ? StatusForm
              : type === 'eq5d'
                ? EQ5DForm
                : type === 'msnq'
                  ? MSNQForm
                  : type === 'fsmc'
                    ? FSMCForm
                    : undefined
          }
          cancelCallback={this.onCancel}
          confirmCallback={this.onConfirm}
          closeCallback={this.onCloseInfo}
          documents={documents}
          infoComponent={infoComponent}
          editingId={editingId}
          viewingId={viewingId}
          fm={fm}
        />

        <ContentColumns
          firstColumn={
            <div style={{ display: 'flex', marginTop: '2rem' }}>
              <div style={{ flex: 1 }}>
                {view > 1 ? (
                  <ActionButton
                    localeId={'onlineRehabilitation.back'}
                    onClick={this.backView}
                    style={{ width: '15rem' }}
                    secondary
                  />
                ) : null}
              </div>
              {view !== 4 && currentType === 'done' && (
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <ActionButton
                    localeId={view === 8 ? 'onlineRehabilitation.send' : 'onlineRehabilitation.advance'}
                    onClick={this.advanceView}
                    style={{ width: '15rem' }}
                    filledSecondary
                  />
                </div>
              )}
            </div>
          }
        />
      </div>
    );
  }
}

Contents.contextTypes = {
  setFinished: func,
  fm: func,
  create: func,
  cancel: func,
  done: func,
  commitPut: func,
  edit: func,
  editingDocumentId: string,
  onChange: func,
  formValues: object,
};
Contents.propTypes = {
  documents: arrayOf(object),
};

class RehabilitationFollow extends React.Component {
  render() {
    const { documents } = this.props;
    return <Contents documents={documents} />;
  }
}

RehabilitationFollow.propTypes = {
  documents: arrayOf(object),
};

export default RehabilitationFollow;
